import { useEffect, useReducer, useState, type Reducer, type PropsWithChildren } from 'react';
import { UserContext } from 'app/state/contexts';
import config from 'config';
import { useCurrentUser } from 'entities/user';
import { useOrganizations as useOrganizationsQuery } from 'entities/organization';
import i18next from 'shared/i18n/i18next';
import authStore, { type RoleBearingUser } from 'shared/auth/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { organizationKeys } from 'entities/organization';

interface CurrentUser {
  id?: number;
  cultureId?: string;
  userProperties?: { cultureId?: string };
  [key: string]: unknown;
}

interface CurrentUserData {
  user?: CurrentUser;
  permissions: { attributeRoles: { attributeId: number | null; role: { permissions: string[] } }[] };
}

type Organization = NonNullable<ReturnType<typeof useOrganizationsQuery>['data']>[number];

const useOrganizationSelection = (user: CurrentUser | undefined, organizations: Organization[]) => {
  const key = user?.id ? `selected_organization_${user.id}` : null;

  const [selectedOrganization, selectOrganization] = useReducer<Reducer<number | undefined, number>>((_, newOrg) => {
    if (key) {
      window.localStorage.setItem(key, String(newOrg));
    }

    return newOrg;
  }, undefined);

  useEffect(() => {
    if (key) {
      const savedOrgId = localStorage.getItem(key);
      if (savedOrgId !== null) {
        selectOrganization(parseInt(savedOrgId, 10));
      } else if (organizations?.length > 0) {
        selectOrganization(organizations[0].id);
      }
    }
  }, [key, organizations]);

  return {
    selectedOrganization,
    selectOrganization,
  };
};

const PageWrapper = (props: PropsWithChildren<unknown>) => {
  const { user: currentUser, permissions: currentPermissions } = (useCurrentUser()?.data || {}) as CurrentUserData;
  const { data: organizations = [] } = useOrganizationsQuery(!!currentUser);
  const queryClient = useQueryClient();

  const { selectedOrganization, selectOrganization } = useOrganizationSelection(currentUser, organizations);

  const locale = currentUser?.cultureId || currentUser?.userProperties?.cultureId || config.defaultLocale || 'sv';
  const [localeApplied, setLocaleApplied] = useState(false);

  useEffect(() => {
    if (currentUser) {
      authStore.attributeRoles = currentPermissions.attributeRoles;
      authStore.setUserRoles(currentUser as RoleBearingUser);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    setLocaleApplied(false);
    i18next.changeLanguage(locale).finally(() => setLocaleApplied(true));
  }, [locale]);

  const refreshOrganizations = () => {
    queryClient.invalidateQueries({
      queryKey: organizationKeys.list(),
    });
  };

  if (!currentUser || organizations.length < 1 || selectedOrganization === undefined || !localeApplied) {
    return <div>Loading...</div>;
  }

  const userContextValue = {
    user: currentUser,
    refreshOrganizations,
    selectedOrganization,
    organizations,
    selectOrganization,
  };
  return <UserContext.Provider value={userContextValue}>{props.children}</UserContext.Provider>;
};

export default PageWrapper;
